<template>
  <div class="redirect-element">
    <div class="redirect-header">
      <span class="redirect-type-badge">
        <i class="fas fa-random"></i>
        <span>Redirection</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="redirect-content">
      <div class="redirect-form">
        <div class="form-row">
          <div class="redirect-select-container">
            <div class="select-wrapper">
              <i class="fas fa-arrow-right tag-icon"></i>
              <select 
                v-model="selectedBlockId" 
                class="redirect-select"
                @change="handleBlockChange"
              >
                <option value="" disabled>Sélectionner un bloc de destination</option>
                <optgroup v-if="mainBlocks.length > 0" label="Blocs principaux">
                  <option 
                    v-for="block in mainBlocks" 
                    :key="block.idblock" 
                    :value="block.idblock"
                  >
                    {{ block.displayName }}
                  </option>
                </optgroup>
                <optgroup v-if="standardBlocks.length > 0" label="Blocs standards">
                  <option 
                    v-for="block in standardBlocks" 
                    :key="block.idblock" 
                    :value="block.idblock"
                  >
                    {{ block.blockname || block.idblock }}
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="selectedBlockId" class="selected-info">
          <i class="fas fa-check-circle"></i>
          <span>Redirection configurée vers: 
            <router-link 
              :to="{ 
                name: 'playgroundWithBlock', 
                params: { 
                  idchatbot: idBot, 
                  idblock: selectedBlockId 
                }
              }"
              class="block-link"
            >
              <strong>{{ getSelectedBlockName() }}</strong>
            </router-link>
          </span>
        </div>
        
        <div v-else class="no-selection-info">
          <i class="fas fa-exclamation-triangle"></i>
          <span>Aucune destination sélectionnée</span>
        </div>
        
        <div class="redirect-description">
          Redirige le flux vers le bloc sélectionné
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios';

export default {
  name: 'RedirectElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    idBot: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedBlockId: this.element.redirection_block || '',
      blocks: [],
      mainBlocks: [],
      standardBlocks: [],
      isLoading: false
    }
  },
  async created() {
    await this.loadBlocks();
  },
  methods: {
    async loadBlocks() {
      this.isLoading = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return;
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const config = {
          headers: { 'Authorization': `Bearer ${token}` }
        };
        
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.idBot}/flow`,
          config
        );
        
        if (response.data && response.data.success) {
          const flowData = response.data.flow;
          
          // Traiter les blocs principaux
          this.mainBlocks = [];
          if (flowData.mainblocks) {
            if (flowData.mainblocks.welcome) {
              this.mainBlocks.push(...flowData.mainblocks.welcome.map(block => ({
                ...block,
                displayName: `Welcome: ${block.blockname || block.idblock}`
              })));
            }
            if (flowData.mainblocks.default) {
              this.mainBlocks.push(...flowData.mainblocks.default.map(block => ({
                ...block,
                displayName: `Default: ${block.blockname || block.idblock}`
              })));
            }
            if (flowData.mainblocks.commerce) {
              this.mainBlocks.push(...flowData.mainblocks.commerce.map(block => ({
                ...block,
                displayName: `Commerce: ${block.blockname || block.idblock}`
              })));
            }
          }
          
          // Traiter les blocs standards
          this.standardBlocks = flowData.blocks || [];
          
          // Combiner tous les blocs pour la recherche
          this.blocks = [...this.mainBlocks, ...this.standardBlocks];
        }
      } catch (error) {
        console.error('Erreur lors du chargement des blocs:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    handleBlockChange() {
      // Pattern simple comme VariableInsertElement
      this.$emit('update', {
        ...this.element,
        redirection_block: this.selectedBlockId
      });
      
      console.log('RedirectElement - Bloc sélectionné:', this.selectedBlockId);
    },
    
    getSelectedBlockName() {
      const block = this.blocks.find(b => b.idblock === this.selectedBlockId);
      return block ? (block.displayName || block.blockname || block.idblock) : this.selectedBlockId;
    }
  }
}
</script>

<style scoped>
.redirect-element {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.redirect-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, #f8fafc, #f1f5f9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.redirect-type-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #0ea5e9;
  font-weight: 500;
}

.element-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  background: white;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #64748b;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #0ea5e9;
}

.delete-btn:hover {
  background: #fef2f2;
  color: #ef4444;
}

.redirect-content {
  padding: 1.25rem;
}

.redirect-form {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.redirect-select-container {
  flex: 1;
}

.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 12px;
  padding: 0.25rem;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.select-wrapper:hover {
  border-color: #93c5fd;
  background: #ffffff;
}

.select-wrapper:focus-within {
  border-color: #0ea5e9;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.tag-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 8px;
  font-size: 0.75rem;
  margin: 0 0.25rem;
}

.redirect-select {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;
  outline: none;
  appearance: none;
  padding-right: 2rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}

.selected-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #dcfce7;
  border-radius: 8px;
  border: 1px solid #86efac;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #15803d;
}

.no-selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #fff1f2;
  border-radius: 8px;
  border: 1px solid #fecdd3;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #ef4444;
}

.redirect-description {
  font-size: 0.75rem;
  color: #64748b;
  padding-left: 0.5rem;
}

.block-link {
  color: #0ea5e9;
  text-decoration: none;
  transition: color 0.2s ease;
}

.block-link:hover {
  color: #0284c7;
  text-decoration: underline;
}

/* Styles pour les optgroups */
.redirect-select optgroup {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.redirect-select option {
  padding: 8px;
  color: #6b7280;
}
</style>
